import { Location, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  ProviderToken,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import { AssignmentService } from '@examdojo/assignment';
import {
  ChallengeActivityHttpModel,
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityService,
  ChallengeActivityStore,
} from '@examdojo/challenge';
import { ButtonComponent } from '@examdojo/core/button';
import { DebuggingContextService } from '@examdojo/debugging';
import { SmallLayoutComponent, WideLayoutComponent } from '@examdojo/layout';
import { CANVAS_EXPORT_TIMEOUT_MS } from '@examdojo/canvas';
import {
  QUESTION_CONTEXT,
  QUESTION_SELECTOR_DATA_SOURCE,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityStore,
  removeSolutionCaptureQueryParamWithoutReloadState,
} from '@examdojo/question';
import { SyllabusQuery } from '@examdojo/category/v2';
import { PlatformService } from '@examdojo/platform';
import { SolutionCaptureQueryParam } from '@examdojo/solution';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { UserQuery } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
  timeout,
  throwError,
} from 'rxjs';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { rethrowError } from '@examdojo/core/rxjs';
import { ExamdojoQuestionComponent } from '../../../question-v2';
import { BatchGradingEffectService } from '../effects/batch-grading-effect.service';
import { ChallengeActivityMcqResponseRealtimeService } from '../effects/challenge-activity-mcq-response-realtime.service';
import { ChallengeActivityQuestionAttemptsRealtimeService } from '../effects/challenge-activity-question-attempts-realtime.service';
import { ChallengeActivityQuestionStatusEffect } from '../effects/challenge-activity-question-status.effect';
import { CloseSolutionCaptureDialogChallengeEffectService } from '../effects/close-solution-capture-dialog-challenge-effect.service';
import { FetchAssignmentQuestionsOnActiveChallengeActivityService } from '../effects/fetch-assignment-questions-on-active-challenge-activity.service';
import { FetchChallengeActivityQuestionAttemptsOnActiveService } from '../effects/fetch-challenge-activity-question-attempts-on-active.service';
import { FetchMcqResponseOnActiveChallengeActivityService } from '../effects/fetch-mcq-response-on-active-challenge-activity.service';
import { provideMultiAttemptQuestionSubmitReady } from '../effects/multi-attempt-question-submit-ready.service';
import {
  MultiAttemptSelfGradingSelectionService,
  provideMultiAttemptSelfGradingSelection,
} from '../effects/multi-attempt-self-grading-selection.service';
import { QuestionAttemptRealtimeChallengeService } from '../effects/question-attempt-realtime-challenge.service';
import { ForceSelfGradingOnErrorEffect } from '../effects/force-self-grading-on-error.effect';
import { provideSyncedMcqSelection, SyncedMcqSelectionService } from '../effects/synced-mcq-selection.service';
import { ExamTimeComponent, ExamTimeService } from '../exam-time';
import { ChallengeGradingLoadingComponent } from '../grading-loading/challenge-grading-loading.component';
import {
  CanvasExportResult,
  ChallengeCanvasExportService,
  HiddenCanvasExporterComponent,
} from '../hidden-canvas-exporter';
import { LeaveConfirmationDialogService } from '../leave-confirmation/leave-confirmation-dialog.service';
import { ChallengeActivityQuestionSelectorComponent } from '../question-selector/challenge-activity-question-selector.component';
import { ChallengeActivityQuestionStatusSelectorComponent } from '../question-selector/challenge-activity-question-status-selector.component';
import { ChallengeActivityQuestionSelectorService } from '../question-selector/challenge-activity-question-selector.service';
import { SubmissionSpinnerDialogEffect, SubmissionSpinnerDialogService } from '../submission-spinner';
import { SubmitConfirmationDialogService } from '../submit-confirmation/submit-confirmation-dialog.service';
import { ChallengeDialogService } from './challenge-dialog.service';

@Component({
  selector: 'dojo-challenge-dialog-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './challenge-dialog-container.component.html',
  imports: [
    FooterPanelComponent,
    ButtonComponent,
    TranslocoPipe,
    ChallengeActivityQuestionSelectorComponent,
    ChallengeActivityQuestionStatusSelectorComponent,
    ExamdojoQuestionComponent,
    SmallLayoutComponent,
    WideLayoutComponent,
    NgClass,
    ChallengeGradingLoadingComponent,
    ExamTimeComponent,
    HiddenCanvasExporterComponent,
  ],
  animations: [
    getAnimationAppearDisappear({
      leave: AnimationDuration.None,
    }),
  ],
  host: { class: 'flex flex-col h-full' },
  providers: [
    ChallengeActivityQuestionSelectorService,
    { provide: QUESTION_SELECTOR_DATA_SOURCE, useExisting: ChallengeActivityQuestionSelectorService },
    SubmitConfirmationDialogService,
    SubmissionSpinnerDialogService,
    SubmissionSpinnerDialogEffect,
    LeaveConfirmationDialogService,
    ExamTimeService,
    ChallengeCanvasExportService,
    FetchAssignmentQuestionsOnActiveChallengeActivityService,
    FetchChallengeActivityQuestionAttemptsOnActiveService,
    FetchMcqResponseOnActiveChallengeActivityService,
    BatchGradingEffectService,
    QuestionAttemptRealtimeChallengeService,
    ChallengeActivityQuestionAttemptsRealtimeService,
    provideSyncedMcqSelection(),
    provideMultiAttemptSelfGradingSelection(),
    provideMultiAttemptQuestionSubmitReady(),
    ChallengeActivityQuestionStatusEffect,
    ChallengeActivityMcqResponseRealtimeService,
    CloseSolutionCaptureDialogChallengeEffectService,
    ForceSelfGradingOnErrorEffect,
    {
      provide: QUESTION_CONTEXT,
      useFactory: () => {
        const challengeActivityQuery = inject(ChallengeActivityQuery);
        const syllabusQuery = inject(SyllabusQuery);
        const status = toSignal(challengeActivityQuery.activeEntityStatus$);
        const activeProgram = toSignal(syllabusQuery.activeProgram$);

        return {
          showHints: computed(() => status() === 'GRADED'),
          optionsSelectable: true,
          revealSolutionOnWrongAnswer: true,
          showGradingState: true,
          hideDifficulty: computed(() => status() !== 'GRADED'),
          hideCalculatorIndicator: computed(() => activeProgram()?.code === 'AP'),
        };
      },
    },
  ],
})
export class ChallengeDialogContainerComponent {
  constructor(
    private readonly challengeDialogService: ChallengeDialogService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly destroyRef: DestroyRef,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly challengeActivityQuestionSelectorService: ChallengeActivityQuestionSelectorService,
    private readonly submitConfirmationDialogService: SubmitConfirmationDialogService,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly examTimeService: ExamTimeService,
    private readonly multiAttemptSelfGradingSelectionService: MultiAttemptSelfGradingSelectionService,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly assignmentService: AssignmentService,
    private readonly leaveConfirmationDialogService: LeaveConfirmationDialogService,
    private readonly canvasExportService: ChallengeCanvasExportService,
    private readonly userQuery: UserQuery,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly errorHandler: ErrorHandlerService,
    private readonly questionAttemptEntityStore: QuestionAttemptEntityStore,
    private readonly platformService: PlatformService,
    private readonly challengeActivityStore: ChallengeActivityStore,
  ) {
    merge(
      this.debuggingContextService.syncDebuggingContext('Activity id', this.activeChallengeActivityId$),
      this.setSolutionCaptureQueryParamWithoutReload(),
      this.syncActiveAssignment(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();

    this.destroyRef.onDestroy(() => this.challengeActivityStore.setActive(null));
  }

  private readonly instantiatedServices = [
    FetchAssignmentQuestionsOnActiveChallengeActivityService,
    FetchChallengeActivityQuestionAttemptsOnActiveService,
    FetchMcqResponseOnActiveChallengeActivityService,
    BatchGradingEffectService,
    QuestionAttemptRealtimeChallengeService,
    ChallengeActivityQuestionAttemptsRealtimeService,
    SyncedMcqSelectionService,
    MultiAttemptSelfGradingSelectionService,
    ChallengeActivityQuestionStatusEffect,
    ChallengeActivityMcqResponseRealtimeService,
    CloseSolutionCaptureDialogChallengeEffectService,
    ForceSelfGradingOnErrorEffect,
    SubmissionSpinnerDialogEffect,
  ].map((service: ProviderToken<unknown>) => inject(service));

  private readonly questionComponent = viewChild(ExamdojoQuestionComponent);

  readonly pendingCanvasExportAttemptIds = signal<string[]>([]);
  readonly submittingSelfGrading = signal(false);
  private readonly hiddenExports$ = new Subject<CanvasExportResult[]>();

  readonly activeChallengeActivityId$ = this.challengeActivityQuery.activeChallengeActivityId$;

  readonly isTransitioningBetweenQuestions =
    this.challengeActivityQuestionSelectorService.isTransitioningBetweenQuestions;

  readonly isCanvasVisible = computed(() => this.questionComponent()?.isCanvasVisible() ?? false);
  readonly isDesktop = this.platformService.isDesktop;

  private readonly isTimeUp$ = this.examTimeService.isTimeUp$;

  readonly state = connectState({
    activeChallengeActivityId: this.activeChallengeActivityId$,
    challengeActivityStatus: this.challengeActivityQuery.activeEntityStatus$,
    allQuestionAttemptsDone: this.challengeActivityQuestionSelectorService.allQuestionAttemptsDone$,
    activeQuestionAttempt: this.questionAttemptQuery.active$,
    isQuestionMultipleChoice: this.questionAttemptQuery.isQuestionMultipleChoice$,
    shouldShowSubmitButton: this.challengeActivityQuery.activeEntityStatus$.pipe(
      map((status) => status === 'IN_PROGRESS'),
    ),
    shouldShowExitButton: this.challengeActivityQuery.activeEntityStatus$.pipe(
      map((status) => status === 'IN_PROGRESS'),
    ),
    isTimeUp: this.isTimeUp$,
    shouldShowRightSidebar: this.challengeActivityQuery.activeEntity$.pipe(map((activity) => !!activity)),
    userId: this.userQuery.selectActiveId(),
  });

  onQuestionCompleted(action: 'complete' | 'stop') {
    const activityId = this.state.activeChallengeActivityId;
    if (!activityId) {
      this.challengeDialogService.dismiss();
      return;
    }

    if (action === 'stop') {
      from(this.leaveConfirmationDialogService.openDialog())
        .pipe(
          switchMap((confirmed) => (confirmed ? this.completeChallengeActivity(activityId, 'DISCARDED') : EMPTY)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    if (action === 'complete') {
      this.completeChallengeActivity(activityId, 'COMPLETED').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  gradeChallengeActivity() {
    this.examTimeService.isTimeUp$
      .pipe(
        take(1),
        switchMap((isTimeUp) => this.gradeChallengeAfterConfirmation(!isTimeUp)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  submitSelfGradingAndComplete() {
    if (this.submittingSelfGrading()) {
      return;
    }

    const activityId = this.state.activeChallengeActivityId;
    if (!activityId) {
      this.challengeDialogService.dismiss();
      return;
    }

    this.submittingSelfGrading.set(true);

    const allQuestionAttemptIds = this.challengeActivityQuestionAttemptQuery
      .getByChallengeActivityId(activityId)
      .map((qa) => qa.question_attempt_id);

    const submissions$ = this.multiAttemptSelfGradingSelectionService
      .generateAllGradingPayloads(allQuestionAttemptIds)
      .map(({ attemptId, payload }) =>
        this.questionAttemptEntityService
          .submitForSelfGrading(payload, attemptId, { setStatus: false })
          .pipe(catchError(() => of(null))),
      );

    const complete$ =
      submissions$.length === 0
        ? this.completeChallengeActivity(activityId, 'COMPLETED')
        : forkJoin(submissions$).pipe(switchMap(() => this.completeChallengeActivity(activityId, 'COMPLETED')));

    complete$
      .pipe(
        finalize(() => this.submittingSelfGrading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onHiddenExportsReady(exports: CanvasExportResult[]) {
    this.hiddenExports$.next(exports);
  }

  private gradeChallengeAfterConfirmation(showConfirmation: boolean) {
    const activityId = this.state.activeChallengeActivityId;
    if (!activityId) {
      this.challengeDialogService.dismiss();
      return EMPTY;
    }

    const confirmation$ = showConfirmation ? from(this.submitConfirmationDialogService.openDialog()) : of(true);

    return confirmation$.pipe(
      filter(Boolean),
      switchMap(() => this.exportAndSaveFrqCanvases()),
      switchMap(() => this.challengeActivityService.gradeChallengeActivity(activityId)),
      switchMap(() => this.challengeActivityQuestionAttemptService.markAllQuestionAttemptsAsDone(activityId)),
      this.errorHandler.catchError(
        `[ChallengeDialogContainerComponent.gradeChallengeAfterConfirmation]: failed to grade challenge activity`,
        () => EMPTY,
        { toast: `Could not grade paper. Please try again later.` },
      ),
    );
  }

  private exportAndSaveFrqCanvases(): Observable<void> {
    const allFrqAttemptIds = this.canvasExportService.getFrqAttemptIds();

    if (allFrqAttemptIds.length === 0) {
      return of(undefined);
    }

    this.questionAttemptEntityStore.setUploadingSolution(true);

    const currentAttemptId = this.canvasExportService.getCurrentAttemptId();
    const isCurrentFrq = this.canvasExportService.isCurrentAttemptFrq();

    return forkJoin([
      this.exportCurrentCanvas(currentAttemptId, isCurrentFrq),
      this.exportRemainingCanvases(allFrqAttemptIds, currentAttemptId),
    ]).pipe(
      map(([current, remaining]) => [...current, ...remaining]),
      tap(() => this.pendingCanvasExportAttemptIds.set([])),
      switchMap((exports) => this.canvasExportService.saveAllCanvasExports(exports)),
      this.errorHandler.catchError(
        '[ChallengeDialogContainerComponent.exportAndSaveFrqCanvases]: failed to export/save canvases',
        rethrowError(),
      ),
      finalize(() => this.questionAttemptEntityStore.setUploadingSolution(false)),
    );
  }

  private exportCurrentCanvas(
    currentAttemptId: string | undefined,
    isCurrentFrq: boolean,
  ): Observable<CanvasExportResult[]> {
    if (!isCurrentFrq || !currentAttemptId) {
      return of([]);
    }

    const canvas = this.questionComponent()?.canvas();
    if (!canvas) {
      return of([]);
    }

    return from(canvas.exportCanvasPagesAsImages()).pipe(
      map((files) => (files.length > 0 ? [{ attemptId: currentAttemptId, files }] : [])),
    );
  }

  private exportRemainingCanvases(
    allFrqAttemptIds: string[],
    currentAttemptId: string | undefined,
  ): Observable<CanvasExportResult[]> {
    const remainingAttemptIds = allFrqAttemptIds.filter((id) => id !== currentAttemptId);

    if (remainingAttemptIds.length === 0) {
      return of([]);
    }

    this.pendingCanvasExportAttemptIds.set(remainingAttemptIds);
    // hidden tldraw component will render, export the canvases and emit the results through the hiddenExports subject
    return this.hiddenExports$.pipe(
      take(1),
      timeout(CANVAS_EXPORT_TIMEOUT_MS),
      catchError((err: unknown) => {
        this.errorHandler.error(
          '[ChallengeDialogContainerComponent.exportRemainingCanvases]: Timed out waiting for hidden canvas exports',
          {
            err,
            context: {
              remainingAttemptIds,
            },
          },
        );
        return throwError(() => err);
      }),
    );
  }

  private completeChallengeActivity(activityId: string, status: ChallengeActivityHttpModel['status']) {
    return this.challengeActivityService.completeChallengeActivityWithStatus(activityId, status);
  }

  private setSolutionCaptureQueryParamWithoutReload() {
    return this.route.queryParamMap.pipe(
      switchMap((params) => {
        if (!params.get(SolutionCaptureQueryParam)) {
          return EMPTY;
        }

        return this.questionAttemptQuery.active$.pipe(
          filter(Boolean),
          take(1),
          tap(() => {
            this.questionAttemptEntityService.setOpenCaptureDialogFlag(true);
            this.location.replaceState(removeSolutionCaptureQueryParamWithoutReloadState());
          }),
        );
      }),
    );
  }

  private syncActiveAssignment() {
    return this.challengeActivityQuery.activeEntity$.pipe(
      filter(Boolean),
      take(1),
      tap((activity) => {
        if (activity.assignment_section_id) {
          this.assignmentService.setActiveByAssignmentSectionId(activity.assignment_section_id);
        }
      }),
    );
  }
}
